/**
 * 待办离线队列后台同步服务
 * 网络恢复后重试队列，做时间/内容比对合并
 */

import { supabase, isSupabaseAvailable } from '@ext-bookmark/shared/api';
import { getCurrentUser } from '../auth/userService';
import type { Todo } from '@ext-bookmark/shared/types';
import { mergeTodos } from './mergeService';
import {
  getTodoQueue,
  removeTodoOp,
  incrementTodoOpRetry,
  isTodoOpExhausted,
  type TodoQueuedOp,
} from './todoOfflineQueue';
import {
  saveTodoToSupabase,
  deleteTodoFromSupabase,
  deleteTodosFromSupabase,
} from '../todo/supabaseStorage';
import { mapSupabaseRowToTodo } from '../todo/supabaseStorage';

const POLL_INTERVAL_MS = 30 * 1000; // 30s 轮询
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let onlineHandler: (() => void) | null = null;
let started = false;

async function fetchTodoById(todoId: string): Promise<Todo | null> {
  if (!supabase || !isSupabaseAvailable()) return null;
  try {
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .eq('id', todoId)
      .single();
    if (error || !data) return null;
    return mapSupabaseRowToTodo(data);
  } catch {
    return null;
  }
}

/**
 * 合并单条：本地操作 vs 远程最新，取 updatedAt 更大者
 */
function mergeTodoForSync(localTodo: Todo, remoteTodo: Todo | null): Todo {
  if (!remoteTodo) return localTodo;
  const merged = mergeTodos([localTodo], [remoteTodo]);
  return merged[0] ?? localTodo;
}

async function processOp(op: TodoQueuedOp): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user || !isSupabaseAvailable()) return false;

  try {
    if (op.type === 'SAVE_TODO') {
      const todo = op.payload as Todo;
      const remote = await fetchTodoById(todo.id);
      const toSave = mergeTodoForSync(todo, remote);
      const { error } = await saveTodoToSupabase(toSave);
      return !error;
    }

    if (op.type === 'TOGGLE_TODO') {
      const { todoId, instanceDate } = op.payload as { todoId: string; instanceDate?: number };
      const remote = await fetchTodoById(todoId);
      if (!remote) return false;
      let updated: Todo;
      if (remote.recurrence && instanceDate !== undefined) {
        const normalizedDate = new Date(instanceDate);
        normalizedDate.setHours(0, 0, 0, 0);
        const dateTimestamp = normalizedDate.getTime();
        const completedDates = remote.recurrence.completedDates || [];
        const isCompleted = completedDates.includes(dateTimestamp);
        const newCompletedDates = isCompleted
          ? completedDates.filter((d) => d !== dateTimestamp)
          : [...completedDates, dateTimestamp];
        updated = {
          ...remote,
          recurrence: { ...remote.recurrence, completedDates: newCompletedDates },
          updatedAt: Date.now(),
        };
      } else {
        updated = {
          ...remote,
          completed: !remote.completed,
          completedAt: !remote.completed ? Date.now() : undefined,
          updatedAt: Date.now(),
        };
      }
      const { error } = await saveTodoToSupabase(updated, { skipExistingFetch: true });
      return !error;
    }

    if (op.type === 'DELETE_TODO') {
      const { todoId } = op.payload as { todoId: string };
      const { error } = await deleteTodoFromSupabase(todoId);
      return !error;
    }

    if (op.type === 'DELETE_TODOS') {
      const { todoIds } = op.payload as { todoIds: string[] };
      const { error } = await deleteTodosFromSupabase(todoIds);
      return !error;
    }
  } catch (e) {
    console.warn('[TodoOfflineSync] 处理操作失败:', op.type, e);
  }
  return false;
}

/**
 * 处理队列：逐个重试，成功则移除，失败则增加重试计数
 */
export async function processTodoOfflineQueue(): Promise<void> {
  const user = await getCurrentUser();
  if (!user || !isSupabaseAvailable()) return;

  const queue = getTodoQueue();
  if (queue.length === 0) return;

  for (const op of queue) {
    const success = await processOp(op);
    if (success) {
      removeTodoOp(op.id);
    } else {
      incrementTodoOpRetry(op.id);
      const updated = getTodoQueue().find((o) => o.id === op.id);
      if (updated && isTodoOpExhausted(updated)) {
        removeTodoOp(op.id); // 超过重试次数，移除避免无限重试
        console.warn('[TodoOfflineSync] 操作超过最大重试次数，已移除:', op.type, op.payload);
      }
    }
  }
}

/**
 * 启动后台同步：监听 online 事件，定期轮询（幂等，多次调用仅启动一次）
 */
export function startTodoOfflineSync(): void {
  if (started) return;
  started = true;

  const run = () => {
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      processTodoOfflineQueue().catch(() => {});
    }
  };
  onlineHandler = run;

  if (typeof window !== 'undefined') {
    window.addEventListener('online', run);
    run(); // 启动时立即尝试一次
    pollTimer = setInterval(run, POLL_INTERVAL_MS);
  }
}

/**
 * 停止后台同步
 */
export function stopTodoOfflineSync(): void {
  if (typeof window !== 'undefined' && onlineHandler) {
    window.removeEventListener('online', onlineHandler);
    onlineHandler = null;
  }
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  started = false;
}
